import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Underline } from '@tiptap/extension-underline';
import { Highlight } from '@tiptap/extension-highlight';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { TextAlign } from '@tiptap/extension-text-align';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { Mention } from '@tiptap/extension-mention';
import { Mathematics } from '@tiptap/extension-mathematics';
import { Emoji } from '@tiptap/extension-emoji';
import { Details, DetailsSummary, DetailsContent } from '@tiptap/extension-details';
import { createLowlight, all } from 'lowlight';

import ImageWithSize from '../components/admin/extensions/ImageWithSize';
import CodeBlockWithCaption from '../components/admin/extensions/CodeBlockWithCaption';
import Iframe from '../components/admin/extensions/Iframe';

const lowlight = createLowlight(all);

/**
 * Jeu d'extensions TipTap pour l'affichage en lecture seule.
 *
 * Partagé entre la page article et la page projet : les deux rendent le même
 * document, ils doivent supporter exactement les mêmes nœuds. Sans ça, un
 * tableau ou un bloc de code saisi dans l'éditeur disparaîtrait silencieusement
 * sur l'une des deux pages.
 */
export function createReadOnlyExtensions() {
    return [
        StarterKit.configure({
            codeBlock: false, // remplacé par CodeBlockWithCaption (cf. ci-dessous)
        }),
        CodeBlockWithCaption.configure({ lowlight }),
        LinkExtension.configure({
            openOnClick: false,
            HTMLAttributes: {
                class: 'editor-link',
            },
        }),
        ImageWithSize.configure({
            HTMLAttributes: {
                class: 'editor-image',
            },
            inline: true,
            allowBase64: true,
        }),
        Table.configure({
            resizable: false,
            HTMLAttributes: { class: 'editor-table' },
        }),
        TableRow,
        TableHeader,
        TableCell,
        Underline,
        Highlight.configure({ HTMLAttributes: { class: 'editor-highlight' } }),
        TaskList.configure({ HTMLAttributes: { class: 'editor-task-list' } }),
        TaskItem.configure({ nested: true, HTMLAttributes: { class: 'editor-task-item' } }),
        TextAlign.configure({ types: ['heading', 'paragraph'], alignments: ['left', 'center', 'right', 'justify'] }),
        Subscript,
        Superscript,
        Mention.configure({
            HTMLAttributes: { class: 'editor-mention' },
            renderText: ({ node }) => `@${node.attrs.label || node.attrs.id}`,
        }),
        Mathematics.configure({ regex: /\$([^$]+)\$/g }),
        Emoji,
        Details.configure({ HTMLAttributes: { class: 'editor-details' }, persist: true }),
        DetailsSummary,
        DetailsContent,
        Iframe,
    ];
}

/**
 * Normalise un contenu TipTap venant de l'API : JSON stringifié, objet
 * document, ou vide. Retourne toujours un document valide.
 */
export function parseTiptapContent(content) {
    if (!content) return { type: 'doc', content: [] };
    if (typeof content === 'object') return content;

    try {
        const parsed = JSON.parse(content);
        return parsed && typeof parsed === 'object' ? parsed : { type: 'doc', content: [] };
    } catch (error) {
        return { type: 'doc', content: [] };
    }
}
